import React, { useCallback, useEffect, useState } from 'react';
import { Activity, ChevronRight } from 'lucide-react';
import { getProviderActiveVisit } from '../../services/patientmr/visits.service';
import { VISIT_STATE_CHANGED_EVENT } from '../../constants/navigationEvents';
import { formatElapsed } from './EndVisitSheet';
import { useDeviceClass } from '../../hooks/useOfflineDevice';
import { useNavigate } from 'react-router-dom';
import { visitWorkspacePath } from '../offline/workspace/VisitWorkspaceRoute';

type ActiveSession = { id?: string; visitId?: string; patientId: string; patientName?: string; startTime?: string; syncState?: string };

export interface ActiveVisitBarProps {
  /** Open the patient's chart on the Charting tab */
  onOpen: (patientId: string) => void;
  /** Hidden while that patient's chart is already open (its header shows the visit) */
  hiddenForPatientId?: string | null;
  className?: string;
}

const firstName = (name?: string) => String(name || '').trim().split(/\s+/)[0] || 'Patient';

/**
 * Persistent "visit in progress" bar (plan §6.2): visible on every screen, replaces the old
 * banner that showed a raw patient UUID. Refreshes on visit start/end and every minute.
 */
const ActiveVisitBar: React.FC<ActiveVisitBarProps> = ({ onOpen, hiddenForPatientId, className = '' }) => {
  const [visit, setVisit] = useState<ActiveSession | null>(null);
  const [, setTick] = useState(0);
  const endHere = useDeviceClass() === 'MOBILE';
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      setVisit((await getProviderActiveVisit()) as unknown as ActiveSession | null);
    } catch {
      setVisit(null);
    }
  }, []);

  useEffect(() => {
    load();
    window.addEventListener(VISIT_STATE_CHANGED_EVENT, load);
    const t = setInterval(() => setTick((n) => n + 1), 60000);
    return () => { window.removeEventListener(VISIT_STATE_CHANGED_EVENT, load); clearInterval(t); };
  }, [load]);

  if (!visit?.patientId || visit.patientId === hiddenForPatientId) return null;
  return (
    <button
      type="button"
      onClick={() => {
        // Phase 16C / 18U U1: on a phone or tablet (and for a visit not synced yet) the bar opens
        // the visit screen; a computer opens the patient's chart on the Charting tab
        const id = visit.id || visit.visitId;
        if (id && (endHere || (visit.syncState && visit.syncState !== 'synced'))) navigate(visitWorkspacePath(id, visit.patientId));
        else onOpen(visit.patientId);
      }}
      className={`flex min-h-[48px] w-full items-center gap-3 bg-emerald-700 px-4 text-left text-sm font-medium text-white ${className}`}
    >
      <Activity size={18} className="shrink-0 animate-pulse" />
      <span className="min-w-0 flex-1 truncate">In visit · {firstName(visit.patientName)}{visit.startTime ? ` · ${formatElapsed(visit.startTime)}` : ''}{visit.syncState && visit.syncState !== 'synced' ? ' · not synced' : ''}</span>
      <span className="flex items-center gap-1 text-xs font-semibold uppercase">{endHere ? 'Chart / End' : 'Chart'}<ChevronRight size={16} /></span>
    </button>
  );
};

export default ActiveVisitBar;
